import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Popover,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import MoreVertOutlinedIcon from "@mui/icons-material/MoreVertOutlined";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTranslation } from "react-i18next";
import { Project } from "../types";
import ConfirmDialog from "./Common/ConfirmDialog";
import useAcl from "../hooks/useAcl";

interface ProjectHeaderProps {
  project: Project | null;
  isEditing: boolean;
  anchorEl: HTMLElement | null;
  handleOpenMenu: (event: React.MouseEvent<HTMLElement>) => void;
  handleCloseMenu: () => void;
  setIsEditing: (value: boolean) => void;
  handleDelete: () => Promise<void>;
  handleCancel?: () => void;
  handleSave: () => void;
  error: string | null;
  setError: (value: string | null) => void;
  success: string | null;
  setSuccess: (value: string | null) => void;
  isLoading: boolean;
}

const ProjectHeader: React.FC<ProjectHeaderProps> = ({
  project,
  isEditing,
  anchorEl,
  handleOpenMenu,
  handleCloseMenu,
  setIsEditing,
  handleDelete,
  handleCancel,
  handleSave,
  error,
  setError,
  success,
  setSuccess,
  isLoading,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { canDelete, canEdit } = useAcl("Project", project);

  const handleOpenConfirm = () => {
    setConfirmOpen(true);
    handleCloseMenu();
  };

  const handleConfirmDelete = async () => {
    if (!project || project.id === "new") return;
    try {
      await handleDelete();
      setConfirmOpen(false);
    } catch (err: any) {
      setConfirmOpen(false);
    }
  };

  const handleCancelDelete = () => {
    setConfirmOpen(false);
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Box sx={{ display: "flex" }}>
          <Button
            startIcon={<ArrowBackIosNewIcon />}
            onClick={() => navigate("/projects")}
            sx={{
              textTransform: "none",
              px: "0",
              color: "#757575",
              minWidth: "auto",
            }}
            disabled={isLoading}
          />
          <Typography
            variant="h5"
            sx={{ fontSize: { xs: "1.5rem", sm: "2rem" } }}
          >
            {project?.name || t("projects.detail.title") || "Проект"}
          </Typography>
        </Box>
        <Box
          sx={{
            transition: "opacity 0.3s ease, transform 0.3s ease",
            opacity: isEditing ? 0 : 1,
            transform: isEditing ? "translateY(-10px)" : "translateY(0)",
            pointerEvents: isEditing ? "none" : "auto",
          }}
        >
          {!isEditing && (
            <IconButton onClick={handleOpenMenu} disabled={isLoading}>
              <MoreVertOutlinedIcon />
            </IconButton>
          )}
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          width: "100%",
          transition: "opacity 0.3s ease, transform 0.3s ease",
          opacity: isEditing ? 1 : 0,
          transform: isEditing ? "translateY(0)" : "translateY(10px)",
          pointerEvents: isEditing ? "auto" : "none",
          height: isEditing ? "auto" : 0,
          overflow: "hidden",
        }}
      >
        {isEditing && (
          <>
            <Button
              variant="outlined"
              onClick={handleCancel}
              sx={{
                textTransform: "none",
                minWidth: { xs: "120px", sm: "150px" },
                boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)",
              }}
              disabled={isLoading}
            >
              {t("tasks.detail.cancelButton")}
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              sx={{
                textTransform: "none",
                minWidth: { xs: "120px", sm: "150px" },
              }}
              disabled={isLoading}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                t("tasks.detail.saveButton")
              )}
            </Button>
          </>
        )}
      </Box>
      {!isEditing && project?.id !== "new" && (
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={handleCloseMenu}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          {canEdit && (
            <MenuItem
              onClick={() => {
                setIsEditing(true);
                setTimeout(() => handleCloseMenu(), 0);
              }}
            >
              <ListItemIcon>
                <EditIcon fontSize="small" sx={{ color: "#4467A5" }} />
              </ListItemIcon>
              <ListItemText primary={t("tasks.detail.editButton")} />
            </MenuItem>
          )}
          {canDelete && (
            <MenuItem onClick={handleOpenConfirm}>
              <ListItemIcon>
                <DeleteIcon fontSize="small" sx={{ color: "#D32F2F" }} />
              </ListItemIcon>
              <ListItemText primary={t("tasks.detail.deleteButton")} />
            </MenuItem>
          )}
        </Popover>
      )}
      <ConfirmDialog
        open={confirmOpen}
        title={
          t("projects.detail.deleteConfirmTitle") || "Подтверждение удаления"
        }
        content={
          t("projects.detail.deleteConfirmContent") ||
          "Вы уверены, что хотите удалить этот проект?"
        }
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        loading={isLoading}
      />
    </>
  );
};

export default ProjectHeader;